/**
 * useSchemaComparison — manages schema comparison API state
 *
 * Returns helpers to compare a file or Databricks table against a data
 * object's stored schema and exposes the result, loading, and error state.
 */

import { useState, useCallback, useMemo } from 'react';
import type { SchemaComparisonResponse, SchemaValidationStatus } from '../types';
import { schemaComparisonService } from '../services/schemaComparisonService';

export interface UseSchemaComparisonReturn {
    result: SchemaComparisonResponse | null;
    loading: boolean;
    error: string | null;
    validationStatus: SchemaValidationStatus | null;
    hasExtraColumns: boolean;
    extraColumnNames: string[];
    compareWithFile: (dataObjectId: string, file: File) => Promise<SchemaComparisonResponse | null>;
    compareWithTable: (dataObjectId: string, tableName: string) => Promise<SchemaComparisonResponse | null>;
    reset: () => void;
}

export function useSchemaComparison(): UseSchemaComparisonReturn {
    const [result, setResult] = useState<SchemaComparisonResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validationStatus = useMemo<SchemaValidationStatus | null>(() => {
        if (!result) return null;
        if (result.is_completely_different) return 'completely_different';
        if (!result.is_compatible) return 'incompatible';
        return 'compatible';
    }, [result]);

    const hasExtraColumns = useMemo(() => (result?.extra_columns?.length ?? 0) > 0, [result]);
    const extraColumnNames = useMemo(() => result?.extra_columns ?? [], [result]);

    const compareWithFile = useCallback(async (dataObjectId: string, file: File) => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await schemaComparisonService.compareSchemaWithFile(dataObjectId, file);
            setResult(res);
            return res;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Schema comparison failed';
            setError(message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const compareWithTable = useCallback(async (dataObjectId: string, tableName: string) => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await schemaComparisonService.compareSchemaWithTable(dataObjectId, tableName);
            setResult(res);
            return res;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Schema comparison failed';
            setError(message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
        setLoading(false);
    }, []);

    return {
        result,
        loading,
        error,
        validationStatus,
        hasExtraColumns,
        extraColumnNames,
        compareWithFile,
        compareWithTable,
        reset,
    };
}
